<template>
    <div
        class="global-search w-full"
        :class="props.watchlistPk ? 'pa-4 alt-style' : ''"
        v-click-outside="
            () => {
                if (props.enableClickOutside) {
                    showResults = false
                }
            }
        "
    >
        <AonInput
            ref="searchInput"
            v-model="searchTerm"
            search-input
            id="global-search"
            :placeholder="t(`search.placeholder.${context}`)"
            @clear="clearSearch"
            @click="onClick"
            @keyup="handleKeyup"
        />
        <div
            class="dropdown-results white w-100"
            :class="[
                { 'elevation-5': !props.watchlistPk },
                { 'alt-style': props.watchlistPk },
                { 'add-border': companyResults.length > 0 || loadingResults },
            ]"
        >
            <AonSpinner v-if="loadingResults" class="ma-auto" :scale="0.5" />
            <!-- // TODO DRY up these two very similar context results blocks -->
            <template v-if="resultsVisible">
                <div class="company-results">
                    <div
                        v-if="!props.watchlistPk"
                        class="totals d-flex align-items-center justify-content-between px-2 py-3"
                    >
                        <div class="wrap d-flex align-items-center large bold grey02--text">
                            <font-awesome-icon :icon="`fas fa-building`" />
                            <p class="grey02--text bold ml-2">
                                {{ t('search.companies') }} <span>{{ companyResultsTotal }}</span>
                            </p>
                        </div>
                        <p
                            class="link bold"
                            data-cy="search-all-companies"
                            @click="searchAllPage('companies')"
                        >
                            {{ t('search.search.companies') }}
                        </p>
                    </div>
                    <div class="results">
                        <p v-if="!companyResults.length" class="text-center pb-5">
                            {{ t('search.noResults') }}
                        </p>
                        <template v-else>
                            <template
                                v-for="(result, index) in companyResults"
                                :key="`highlight_company_${index}`"
                            >
                                <!-- Add divider before the first item with patent_count_us = 0 -->
                                <template
                                    v-if="
                                        result.patent_count_us === 0 &&
                                        ((index > 0 &&
                                            companyResults[index - 1].patent_count_us > 0) ||
                                            index === 0)
                                    "
                                >
                                    <hr class="my-2" />
                                    <p class="grey02--text italic px-2 mb-2">
                                        Results with no US patents
                                    </p>
                                </template>
                                <Highlighter
                                    class="result"
                                    :targetWords="result.name"
                                    :searchTerm="searchTerm"
                                    :ticker="result.stock_ticker"
                                    :company-pk="result.aon_entity_pk.toString()"
                                    :watchlistPk="props.watchlistPk"
                                    :company-params="result"
                                    @clear="clearSearch()"
                                />
                            </template>
                        </template>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash-es'
import { search } from '@/api/entities'

import Highlighter from './helpers/Highlighter'
// TODO use AonMenu instead of doing a one-off custom dropDown

const props = defineProps({
    context: {
        type: String,
        default: 'companies',
    },
    ignoreEnter: {
        type: Boolean,
        default: false,
    },
    watchlistPk: {
        type: String,
        default: null,
    },
    enableClickOutside: {
        type: Boolean,
        default: false,
    },
})

const logger = inject('logger')
const eventBus = inject('eventBus')
const router = useRouter()
const { t } = useI18n()

const companyResults = ref([])
const companyResultsTotal = ref(0)
const loadingResults = ref(false)
const marketResults = ref([])
const marketResultsTotal = ref(0)
const searchInput = ref('searchInput')
const searchTerm = ref('')
const showResults = ref(false)

const isSearchTermEmpty = computed(() => {
    return !searchTermTrimmed.value
})
const resultsVisible = computed(() => {
    return !loadingResults.value && showResults.value
})
const searchTermTrimmed = computed(() => {
    return searchTerm.value ? searchTerm.value.trim() : searchTerm.value
})

onMounted(async () => {
    await nextTick()
    setFocus()
    eventBus.on('update-watchlist', () => {
        clearSearch()
    })
})

const clearSearch = () => {
    searchTerm.value = ''
    showResults.value = false
    setFocus()
    companyResults.value = []
    companyResultsTotal.value = 0
    marketResults.value = []
    marketResultsTotal.value = 0
    loadingResults.value = false
}
const getScreenerFilter = (context) => {
    if (!searchTermTrimmed.value.length) {
        return
    }

    const filterColumn = context === 'companies' ? 'name' : 'aon_sector_name'

    return btoa(
        JSON.stringify({
            filters: [
                {
                    filterColumn,
                    filterType: 'complex_text',
                    filterLabel: 'Name',
                    filterValue: searchTermTrimmed.value,
                },
            ],
        })
    )
}
const liveSearch = debounce(async () => {
    if (searchTermTrimmed.value.length < 2) {
        return
    }

    // TODO handle async API requests with a fetch API abort(if applicable to the POST search request) OR a cancellationToken(deprecated)
    if (isSearchTermEmpty.value) {
        clearSearch()
        return
    }

    loadingResults.value = true
    await searchCompanies()
    showResults.value = true

    loadingResults.value = false
}, 750)

const handleKeyup = (event) => {
    if (event.key !== 'Enter') {
        liveSearch()
    } else {
        if (!props.ignoreEnter) {
            liveSearch.cancel()
        }

        searchAllPage(null)
    }
}

const onClick = () => {
    showResults.value = isSearchTermEmpty.value ? false : true
}

const searchCompanies = async () => {
    const params = {
        //FV: [searchTermTrimmed.value],
        page_size: 5,
    }

    try {
        const response = await search(searchTermTrimmed.value, params)
        if (response.status !== 200) {
            companyResults.value = []
            companyResultsTotal.value = 0
            throw new Error(`Companies search failure for ${searchTermTrimmed.value}`)
        }
        companyResultsTotal.value = response.data.rowCount.toLocaleString()
        companyResults.value = response.data.results.sort((a, b) => {
            // If both have patents or both don't have patents, maintain original order
            if (
                (a.patent_count_us > 0 && b.patent_count_us > 0) ||
                (a.patent_count_us === 0 && b.patent_count_us === 0)
            ) {
                return 0
            }
            // Companies with patents come first
            return b.patent_count_us > 0 ? 1 : -1
        })
    } catch (err) {
        logger.error(err)
    }
}
const searchAllPage = async (context) => {
    if (props.ignoreEnter && !context) {
        return
    }

    if (!searchTermTrimmed.value.length && !context) {
        return
    }

    const filter = getScreenerFilter('companies')
    const query = filter ? { filter } : null

    await router.push({
        path: `/companies/search`,
        query,
    })
}
const setFocus = () => {
    if (searchInput.value) {
        setTimeout(() => {
            searchInput.value.setFocus()
        }, 100)
    }
}
</script>

<style lang="scss">
.global-search {
    position: relative;

    &.alt-style {
        width: 900px;
    }

    .dropdown-results {
        position: absolute;
        top: 43px;
        left: 0;
        z-index: 2;

        &.alt-style {
            top: 78px;
        }

        &.add-border {
            border-top: solid 1px vars.$grey01;
            border-radius: 0 0 4px 4px;
        }

        .totals {
            &:nth-of-type(1) {
                border-top: solid 1px grey;
            }
        }
    }

    .result {
        transition: all ease-in-out 0.3s;
        cursor: pointer;
    }
}
</style>
